import React from 'react';
import { useTheme, createUseStyles } from 'react-jss';
import CircleIcon from '../icons/CircleIcon';
import TriangleIcon from '../icons/TriangleIcon';
import SquareIcon from '../icons/SquareIcon';
import StarIcon from '../icons/StarIcon';
import UltimateIcon from '../icons/UltimateIcon';

const SHAPE_ICONS = {
  circle: CircleIcon,
  triangle: TriangleIcon,
  square: SquareIcon,
  star: StarIcon,
  special: UltimateIcon,
};

/**
 * Pressed-tin tile.
 *
 * - Walls are standing bars: the 4px walnut border plus a lit top edge and a
 *   shadow cast onto the tile, so a wall reads as having height.
 * - Tiles carry a hairline grid rule instead of a transparent 1px border.
 * - Destination highlights are corner brackets (a landing pad) in the selected
 *   robot's color, not a dashed outline.
 */

const WALL_LIGHT = {
  top: ['inset 0 4px 0 -2px rgba(255,255,255,0.18)', 'inset 0 7px 5px -5px rgba(42,26,14,0.5)'],
  bottom: ['inset 0 -4px 0 -2px rgba(255,255,255,0.10)', 'inset 0 -7px 5px -5px rgba(42,26,14,0.5)'],
  left: ['inset 4px 0 0 -2px rgba(255,255,255,0.14)', 'inset 7px 0 5px -5px rgba(42,26,14,0.5)'],
  right: ['inset -4px 0 0 -2px rgba(255,255,255,0.14)', 'inset -7px 0 5px -5px rgba(42,26,14,0.5)'],
};

function wallShadow(tile) {
  const shadows = [];
  if (tile.upperWall) shadows.push(...WALL_LIGHT.top);
  if (tile.bottomWall) shadows.push(...WALL_LIGHT.bottom);
  if (tile.leftWall) shadows.push(...WALL_LIGHT.left);
  if (tile.rightWall) shadows.push(...WALL_LIGHT.right);
  return shadows.length ? shadows.join(',') : 'none';
}

const GRID_RULE = '1px solid rgba(42,26,14,0.10)';

const useStyles = createUseStyles({
  container: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    position: 'relative',
    backgroundColor: ({ theme, tile, selectedColor }) => {
      if (tile.isCenter) return 'transparent';
      return tile.possibleDestination ? `${selectedColor || '#b8d4a8'}2e` : theme.colorPrimary;
    },
    borderTop: ({ theme, tile }) => {
      if (tile.upperWall) return `4px solid ${theme.colorWall}`;
      return 'none';
    },
    borderRight: ({ theme, tile }) => {
      if (tile.rightWall) return `4px solid ${theme.colorWall}`;
      if (tile.isCenter) return 'none';
      return GRID_RULE;
    },
    borderBottom: ({ theme, tile }) => {
      if (tile.bottomWall) return `4px solid ${theme.colorWall}`;
      if (tile.isCenter) return 'none';
      return GRID_RULE;
    },
    borderLeft: ({ theme, tile }) => {
      if (tile.leftWall) return `4px solid ${theme.colorWall}`;
      return 'none';
    },
    boxShadow: ({ tile }) => wallShadow(tile),
    borderRadius: ({ tile }) => {
      if (!tile.isCenter) return 0;
      const { x, y } = tile;
      const tl = (x === 7 && y === 7) ? '7px' : '0';
      const tr = (x === 8 && y === 7) ? '7px' : '0';
      const br = (x === 8 && y === 8) ? '7px' : '0';
      const bl = (x === 7 && y === 8) ? '7px' : '0';
      return `${tl} ${tr} ${br} ${bl}`;
    },
    overflow: ({ tile }) => tile.isCenter ? 'hidden' : 'visible',
    cursor: ({ tile }) => tile.isCenter ? 'default' : (tile.possibleDestination ? 'pointer' : 'default'),
    transition: 'background-color 0.15s ease',
  },
  tick: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '5px',
    height: '5px',
    margin: '-2.5px 0 0 -2.5px',
    borderRadius: '50%',
    backgroundColor: ({ theme }) => theme.colorWall,
    opacity: 0.16,
  },
  pad: {
    position: 'absolute',
    inset: '5px',
    pointerEvents: 'none',
  },
  corner: {
    position: 'absolute',
    width: '9px',
    height: '9px',
    borderStyle: 'solid',
    borderColor: ({ selectedColor }) => selectedColor || '#2a63a8',
    borderWidth: 0,
  },
  cornerTL: { top: 0, left: 0, borderTopWidth: '2.5px', borderLeftWidth: '2.5px', borderRadius: '2px 0 0 0' },
  cornerTR: { top: 0, right: 0, borderTopWidth: '2.5px', borderRightWidth: '2.5px', borderRadius: '0 2px 0 0' },
  cornerBL: { bottom: 0, left: 0, borderBottomWidth: '2.5px', borderLeftWidth: '2.5px', borderRadius: '0 0 0 2px' },
  cornerBR: { bottom: 0, right: 0, borderBottomWidth: '2.5px', borderRightWidth: '2.5px', borderRadius: '0 0 2px 0' },
  centerPiece: {
    position: 'absolute',
    inset: 0,
    background: ({ theme }) => theme.colorCenter || '#241a10',
    backgroundImage:
      'linear-gradient(45deg, rgba(240,210,160,0.07) 25%, transparent 25%), linear-gradient(-45deg, rgba(240,210,160,0.07) 25%, transparent 25%)',
    backgroundSize: '10px 10px',
    pointerEvents: 'none',
  },
  missionIcon: {
    position: 'absolute',
    inset: '3px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    filter: ({ tile }) => tile.mission?.active
      ? 'drop-shadow(0 1px 2px rgba(42,26,14,0.45)) drop-shadow(0 0 4px rgba(255,255,255,0.45))'
      : 'drop-shadow(0 1px 1px rgba(42,26,14,0.3))',
    animation: ({ tile }) => tile.mission?.active ? '$glow 2s ease-in-out infinite' : 'none',
  },
  '@keyframes glow': {
    '0%, 100%': { transform: 'scale(1)', opacity: 0.95 },
    '50%': { transform: 'scale(1.1)', opacity: 1 },
  },
});

function Tile({ tile, onTileClick, selectedColor }) {
  const theme = useTheme();
  const classes = useStyles({ theme, tile, selectedColor });

  const MissionShape = tile.mission ? SHAPE_ICONS[tile.mission.shape] ?? UltimateIcon : null;

  return (
    <div className={classes.container} onClick={() => onTileClick(tile)}>
      {tile.isCenter && <div className={classes.centerPiece} />}

      {tile.mission && MissionShape && (
        <div className={classes.missionIcon}>
          <MissionShape color={tile.mission.color} />
        </div>
      )}

      {!tile.isCenter && !tile.mission && !tile.possibleDestination && (
        <div className={classes.tick} />
      )}

      {tile.possibleDestination && !tile.isCenter && (
        <div className={classes.pad}>
          <div className={`${classes.corner} ${classes.cornerTL}`} />
          <div className={`${classes.corner} ${classes.cornerTR}`} />
          <div className={`${classes.corner} ${classes.cornerBL}`} />
          <div className={`${classes.corner} ${classes.cornerBR}`} />
        </div>
      )}
    </div>
  );
}

export default Tile;
